import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AppLoader } from '@hero/atoms';
import { configs } from '@hero/configs';
import {
  CARDISLE_GREETING_CARD,
  CONTACT_TYPE,
  GIFT_MESSAGE_TYPE,
  RECOGNITION_CREATION_STEPS
} from '@hero/constants';
import { useRecognitionCreationHeader } from '@hero/hooks';
import { PageLayout } from '@hero/layouts';
import { RecognitionCustomizeMessageModal } from '@hero/molecules';
import { CreationStepExperience } from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import {
  useGetRecognitionRecipientList,
  useUpdateRecognition
} from '@hero/services/order-service/recognition/hooks';
import { useGetBranding } from '@hero/services/organization-service/hooks';
import { useNewRecognitionStore } from '@hero/store/hooks';
import { useValidateRules } from '@hero/store/hooks/useValidateRules';
import { cleanObjectEmptyFields, getAddonData } from '@hero/utils';
import {
  getBrandingRule,
  getMessageAndCardParamsRule,
  getMessageParamsRule
} from '@hero/validations';

import * as S from './style';

function NewRecognitionExperience() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    code,
    contacts,
    experienceSettings: { messageParams, branding, occasion } = {},
    settings,
    addons,
    onUpdateBranding,
    onUpdateOccasion,
    onUpdateMessageParams,
    updateAddons,
    reset
  } = useNewRecognitionStore();

  const { mutateAsync: updateRecognition, isLoading: isLoadingUpdateRecognition } =
    useUpdateRecognition();
  const { data: { branding: defaultBranding } = {} } = useGetBranding();

  const [isPageLoading, setIsPageLoading] = React.useState(false);
  const header = useRecognitionCreationHeader({
    step: 4,
    setIsPageLoading
  });
  const { data: customMessagesRecipients } = useGetRecognitionRecipientList(
    {
      code,
      options: {
        exists: [
          {
            field: 'experienceSettings.customizedGiftMessage',
            exist: true
          }
        ]
      }
    },
    { enabled: !!code }
  );

  const digitalGreetingCard = getAddonData(addons, CARDISLE_GREETING_CARD);

  const giftMessageType = useMemo(() => {
    if (digitalGreetingCard) return GIFT_MESSAGE_TYPE.cardMessage;
    if (messageParams?.message) return GIFT_MESSAGE_TYPE.inputMessage;
    return null;
  }, [digitalGreetingCard, messageParams]);

  const [activeGiftMessage, setActiveGiftMessage] = useState(giftMessageType);

  const customMessageLength = useMemo(() => {
    return customMessagesRecipients?.totalDocs;
  }, [customMessagesRecipients]);

  const { validate } = useValidateRules();

  const onNextButtonClick = async () => {
    const giftMessageRules = configs?.experience?.greetingCardEnabled
      ? {
          rule: getMessageAndCardParamsRule(activeGiftMessage),
          value: { ...messageParams, digitalGreetingCard: !!digitalGreetingCard }
        }
      : { rule: getMessageParamsRule(), value: messageParams };

    const isValid = await validate([
      { rule: getBrandingRule(), value: branding },
      { ...giftMessageRules }
    ]);

    if (!isValid) return;

    setIsPageLoading(true);
    try {
      await updateRecognition({
        params: { recognitionCode: code },
        body: {
          meta: {
            creationStep: RECOGNITION_CREATION_STEPS.newRecognitionReviewPay
          },
          experienceSettings: { messageParams, branding, occasion },
          settings: cleanObjectEmptyFields(settings),
          addons
        }
      });

      navigate(ROUTE_PATHS.NEW_RECOGNITION_REVIEW_PAY(code), { replace: true });

      setTimeout(() => {
        reset();
      }, 100);
    } finally {
      setIsPageLoading(false);
    }
  };

  useEffect(() => {
    if (!code) {
      navigate(ROUTE_PATHS.MY_RECOGNITIONS);
    }
  }, []);

  useEffect(() => {
    onUpdateBranding({
      logoUrl: branding?.logoUrl || defaultBranding?.logoUrl,
      giftWrapperUrl: branding?.giftWrapperUrl || defaultBranding?.giftWrappers?.default
    });
  }, [defaultBranding]);

  return (
    <PageLayout
      header={header}
      navigation={{
        leftButton: {
          children: t('back'),
          onClick: () => navigate(ROUTE_PATHS.NEW_RECOGNITION_RECIPIENTS)
        },
        rightButton: {
          children: t('next'),
          onClick: onNextButtonClick,
          loading: isLoadingUpdateRecognition,
          icon: true
        }
      }}
    >
      <S.HeadTitle variant="subtitle4Bold">{t('experienceHeaderSubTitle')}</S.HeadTitle>
      <CreationStepExperience
        messageParams={messageParams}
        branding={branding}
        onUpdateBranding={onUpdateBranding}
        onUpdateOccasion={onUpdateOccasion}
        onUpdateMessageParams={onUpdateMessageParams}
        onUpdateAddons={updateAddons}
        customMessageLength={customMessageLength}
        shortcutTagsActive={contacts?.type === CONTACT_TYPE.list}
        giftMessageType={giftMessageType}
        activeGiftMessage={activeGiftMessage}
        setActiveGiftMessage={setActiveGiftMessage}
        addons={addons}
        renderCustomizeMessageModal={({
          isOpenCustomizeMessageModal,
          setOpenCustomizeMessageModal
        }) => {
          return (
            <RecognitionCustomizeMessageModal
              code={code}
              editable
              defaultMessage={messageParams?.message}
              isOpen={isOpenCustomizeMessageModal}
              setIsOpen={setOpenCustomizeMessageModal}
            />
          );
        }}
      />
      {isPageLoading && <AppLoader />}
    </PageLayout>
  );
}

export { NewRecognitionExperience };
